/**
 * Agent tab — read-only display of the org-level agent identity + voice
 * slate that the user's agent runs against. ORG_ADMIN edits happen on
 * the lexserver org rail (`PATCH /api/orgs/:orgId/runtime-config`),
 * which is the existing org-admin surface at `/org/runtime-config`
 * (Phase 8 — `@lexiun/org-admin` lib). This tab links there rather
 * than duplicating the editor inline — keeps the source of truth for
 * the org-rail editor in one place across LexIDE + LexPortal.
 *
 * Surfaces today:
 *   - `org.agents.defaultIdentity.{name, avatarUrl}`
 *   - `org.voice.{provider, allowedVoices, defaultVoice, rate}`
 *
 * Forward-looking (when the lexserver org-rail client surface is
 * promoted into a shared widget): swap the link for an inline editor
 * here. Today the link keeps drift between this view and the canonical
 * editor at zero.
 */

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, ExternalLink, Lock } from "lucide-react";
import { AgentIncognitoIcon } from "@/components/icons/AgentIncognitoIcon";
import { Card } from "@/components/ui/Card";
import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useOpenclawSettings } from "./OpenclawSettingsTabs";

export function AgentTab() {
  const { t } = useTranslation();
  const { config } = useOpenclawSettings();
  const navigate = useNavigate();
  const { identity } = useAuthIdentity();
  const isOrgAdmin = identity?.isOrgAdmin ?? false;

  const identityBlock = config.org.agents?.defaultIdentity;
  const voice = config.org.voice;

  return (
    <Card variant="surface" data-testid="openclaw-agent-tab">
      <div className="flex items-center gap-2">
        <AgentIncognitoIcon size={18} className="text-cream-500" />
        <h3 className="font-serif-display text-display-sm text-coffee-900 dark:text-cream-100">
          {t("settings.openclaw.agent.title")}
        </h3>
        <span
          title={t("settings.openclaw.agent.managedByOrgTooltip")}
          className="inline-flex items-center gap-1 rounded bg-cream-100 px-1.5 py-0.5 text-[10px] font-medium text-coffee-500 dark:bg-coffee-800 dark:text-cream-400"
        >
          <Lock size={9} />
          {t("settings.openclaw.agent.managedByOrg")}
        </span>
      </div>
      <p className="mt-3 text-body-sm text-coffee-600 dark:text-cream-400">
        {t("settings.openclaw.agent.caption")}
      </p>

      <div className="mt-5 flex flex-col gap-4 text-body-sm">
        <Section title={t("settings.openclaw.agent.identityTitle")}>
          <Row
            label={t("settings.openclaw.agent.identityNameLabel")}
            value={identityBlock?.name}
          />
          <Row
            label={t("settings.openclaw.agent.identityAvatarLabel")}
            value={identityBlock?.avatarUrl}
            mono
          />
        </Section>

        <Section title={t("settings.openclaw.agent.voiceTitle")}>
          <Row
            label={t("settings.openclaw.agent.voiceProviderLabel")}
            value={voice?.provider}
            mono
          />
          <Row
            label={t("settings.openclaw.agent.voiceDefaultLabel")}
            value={voice?.defaultVoice}
            mono
          />
          <Row
            label={t("settings.openclaw.agent.voiceAllowedLabel")}
            value={voice?.allowedVoices?.join(", ")}
            mono
          />
          <Row
            label={t("settings.openclaw.agent.voiceRateLabel")}
            value={voice?.rate != null ? String(voice.rate) : undefined}
          />
        </Section>
      </div>

      {isOrgAdmin && (
        <div className="mt-5">
          <button
            type="button"
            data-testid="openclaw-agent-edit-on-org-rail"
            // Today the org-admin lib (`@lexiun/org-admin`) doesn't
            // expose a dedicated runtime-config page — the dedicated
            // editor lives behind the planned org-admin-runtime-
            // config-plan slice. Land the ORG_ADMIN on the
            // org-admin home and let them navigate from there; safer
            // than a 404 inside `/org/*`.
            onClick={() => navigate("/org")}
            className="inline-flex items-center gap-1.5 rounded-md border border-cream-200/60 px-3 py-1.5 text-body-sm font-medium text-coffee-700 transition-colors hover:bg-cream-50 hover:text-coffee-900 dark:border-coffee-700/60 dark:text-cream-200 dark:hover:bg-coffee-800/60 dark:hover:text-cream-100"
          >
            <Building2 size={13} aria-hidden />
            {t("settings.openclaw.agent.editOnOrgRail")}
            <ExternalLink size={11} aria-hidden />
          </button>
        </div>
      )}
    </Card>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1 text-caption font-medium uppercase tracking-wide text-coffee-500 dark:text-cream-500">
        {title}
      </p>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
        {children}
      </dl>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string;
  mono?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <dt className="text-coffee-600 dark:text-cream-400">{label}</dt>
      <dd
        className={
          mono
            ? "truncate font-mono text-coffee-800 dark:text-cream-200"
            : "truncate text-coffee-800 dark:text-cream-200"
        }
      >
        {value && value.length > 0 ? value : (
          <span className="italic text-coffee-500 dark:text-cream-500">
            {t("settings.openclaw.agent.unset")}
          </span>
        )}
      </dd>
    </>
  );
}
